// Client-safe tracking-consent helpers. All functions guard on `import.meta.client`
// and return safe defaults on the server so they can be called from anywhere
// (plugins, composables, components) without SSR crashes.
//
// No Vue/Nuxt imports — auto-imported by Nuxt from `app/utils/`.

import { readCookie } from '~/utils/cookies';

// `Navigator.globalPrivacyControl` (GPC) is a non-standard property not present in
// the DOM lib typings. Declare it here so we can read it without an `any` cast.
declare global {
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

// Cross-subdomain opt-out cookie. `1` means the user has opted out of tracking.
const OPT_OUT_COOKIE = 'arvee_dnss';

// One year, in seconds, for the opt-out cookie Max-Age.
const OPT_OUT_MAX_AGE = 31536000;

/**
 * True when the `arvee_dnss` opt-out cookie is present with value `1`.
 */
export function hasOptOutCookie(): boolean {
  return readCookie(OPT_OUT_COOKIE) === '1';
}

/**
 * True when the user has signalled they do not want to be tracked — either via
 * the browser's Global Privacy Control signal or the `arvee_dnss` opt-out cookie.
 */
export function isTrackingOptedOut(): boolean {
  if (!import.meta.client) return false;

  if (
    typeof navigator !== 'undefined' &&
    navigator.globalPrivacyControl === true
  ) {
    return true;
  }

  return hasOptOutCookie();
}

/**
 * Set or clear the cross-subdomain `arvee_dnss` opt-out cookie. Passing `true`
 * writes a one-year cookie; passing `false` expires it (Max-Age=0).
 */
export function setTrackingOptOut(optOut: boolean): void {
  if (!import.meta.client) return;
  if (typeof document === 'undefined') return;

  document.cookie =
    OPT_OUT_COOKIE +
    '=' +
    (optOut ? '1' : '') +
    '; domain=.arvee.io; path=/; Max-Age=' +
    (optOut ? OPT_OUT_MAX_AGE : 0) +
    '; SameSite=Lax';
}
